import { useMemo } from 'react'
import type { ViewPipelineConfig } from './viewConfig'

import {
  searchItems,
  sortItems,
  filterByCategories,
  type SortState,
} from '@/core/lib/viewInput'

function groupItems<T>(list: readonly T[], groupBy: (item: T) => string): Map<string, T[]> {
  const byGroup = new Map<string, T[]>()
  for (const item of list) {
    const id = groupBy(item)
    const bucket = byGroup.get(id)
    if (bucket) bucket.push(item)
    else byGroup.set(id, [item])
  }
  return byGroup
}

export type ViewInputState = {
  sort?: SortState
  search?: string

  filter?: unknown
}

export interface ViewInput<T> {

  rows: T[]

  byGroup?: Map<string, T[]>
}

export function useViewInput<T>(items: T[], config: ViewPipelineConfig<T>, state: ViewInputState): ViewInput<T> {
  const { sort, search, filter } = state
  return useMemo(() => {
    const searched = config.searchFields ? searchItems(items, search ?? '', config.searchFields) : [...items]
    const excludedByCategory = config.filterCategories
      ? (config.excludedFor ? config.excludedFor(filter) : ((filter as Record<string, string[]>) ?? {}))
      : {}
    const categoryFiltered = config.filterCategories
      ? filterByCategories(searched, config.filterCategories, excludedByCategory)
      : searched
    const filtered = config.extraFilter ? config.extraFilter(categoryFiltered, filter) : categoryFiltered
    const rows = config.sortKeys ? sortItems(filtered, sort ?? null, config.sortKeys) : [...filtered]
    const byGroup = config.groupBy ? groupItems(rows, config.groupBy) : undefined
    return { rows, byGroup }
  }, [items, config, sort, search, filter])
}
